import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import SingleSelect from "@/components/controls/SingleSelect";
import DateTimeField from "@/components/controls/DateTimeField";
import ContractField from "./ContractField";
import PaymentSchedule from "./PaymentSchedule";
import {
  ChargeType,
  BillingFrequency
} from "@/pages/settings/pages/products-v2/types";
import { EqPaymentsBillingFrequency } from "@/pages/contracts/types";
import { EQ_PERIOD_LABEL_KEYS } from "../consts";
import { computeRecurringPaymentCount } from "../consts";
import { PaymentType, ScheduledPaymentDraft, SplitPaymentData } from "../types";

interface BillingScheduleSectionProps {
  selectedService: { chargeType: ChargeType };
  billingStartDate: string;
  isDatePickerOpen: boolean;
  onBillingStartDateChange: (date: string) => void;
  onDatePickerToggle: (open: boolean) => void;
  customBillingFrequency: number | null;
  billingFrequencyOptions: { id: BillingFrequency; displayName: string }[];
  isCustomBillingFrequencySelected: boolean;
  showBillingFrequencyField: boolean;
  // A pricing row that fixes the billing frequency fixes the start date too.
  isBillingStartDateDisabled?: boolean;
  showSplitPayment: boolean;
  onShowSplitPayment: () => void;
  onCancelSplitPayment: () => void;
  splitPaymentData: SplitPaymentData | null;
  onSplitPaymentDataChange: (data: SplitPaymentData | null) => void;
  onCustomBillingFrequencyChange: (value: number | null) => void;
  total: number;
  serviceDurationInMonths: number;
  paymentType: PaymentType;
  onPaymentTypeChange: (type: PaymentType) => void;
  scheduledPayments: ScheduledPaymentDraft[];
  onScheduledPaymentsChange: (payments: ScheduledPaymentDraft[]) => void;
  currency?: string | null;
}

const BillingScheduleSection: React.FC<BillingScheduleSectionProps> = ({
  selectedService,
  billingStartDate,
  isDatePickerOpen,
  onBillingStartDateChange,
  onDatePickerToggle,
  customBillingFrequency,
  billingFrequencyOptions,
  isCustomBillingFrequencySelected,
  showBillingFrequencyField,
  isBillingStartDateDisabled = false,
  showSplitPayment,
  onShowSplitPayment,
  onCancelSplitPayment,
  splitPaymentData,
  onSplitPaymentDataChange,
  onCustomBillingFrequencyChange,
  total,
  serviceDurationInMonths,
  paymentType,
  onPaymentTypeChange,
  scheduledPayments,
  onScheduledPaymentsChange,
  currency
}) => {
  const { t } = useTranslation();

  const getFrequencyLabel = (frequency: EqPaymentsBillingFrequency): string =>
    t(
      EQ_PERIOD_LABEL_KEYS[frequency] ??
        "contractTerms.billingFrequencyLabels.months"
    );

  const getPeriodLabel = (
    billingEvery: number,
    period: EqPaymentsBillingFrequency
  ): string => {
    return t("contractTerms.everyNPeriod", {
      count: billingEvery,
      period: getFrequencyLabel(period)
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1 items-center min-h-6 w-full">
        <div className="flex flex-col gap-1 grow items-start justify-center">
          <div className="flex gap-2 items-center px-1 py-0 w-full">
            <div className="flex h-5 items-start">
              <Text variant="body">{t("contractTerms.billingSchedule")}</Text>
            </div>
          </div>
        </div>
      </div>

      <ContractField label={t("contractTerms.billingStartDate")}>
        <DateTimeField
          type="date"
          value={billingStartDate}
          onChange={onBillingStartDateChange}
          isOpen={isDatePickerOpen}
          onOpenChange={onDatePickerToggle}
          name="billing-start-date"
          className="w-full"
          placeholder={t("contractTerms.billingStartDatePlaceholder")}
          disabled={isBillingStartDateDisabled}
        />
      </ContractField>

      {selectedService.chargeType === ChargeType.ONE_TIME && (
        <>
          {!showSplitPayment ? (
            <Button
              variant="tertiary"
              onClick={onShowSplitPayment}
              className="w-fit"
            >
              {t("contractTerms.splitPayment")}
            </Button>
          ) : (
            <PaymentSchedule
              totalAmount={total}
              billingStartDate={billingStartDate}
              currency={currency}
              mode="one-time"
              paymentType={paymentType}
              scheduledPayments={scheduledPayments}
              numberOfPayments={splitPaymentData?.numberOfPayments}
              billingFrequency={splitPaymentData?.billingFrequency}
              onOneTimeEqualPaymentChange={(
                numPayments: number,
                frequency: EqPaymentsBillingFrequency
              ) => {
                onSplitPaymentDataChange({
                  numberOfPayments: numPayments,
                  billingFrequencyLabel: getFrequencyLabel(frequency),
                  billingFrequency: frequency
                });
              }}
              onScheduledPaymentsChange={onScheduledPaymentsChange}
              onPaymentTypeChange={onPaymentTypeChange}
              onCancelSplit={onCancelSplitPayment}
            />
          )}
        </>
      )}

      {selectedService.chargeType !== ChargeType.ONE_TIME && (
        <>
          {showBillingFrequencyField && (
            <ContractField label={t("contractTerms.billingFrequency")} required>
              <SingleSelect
                fieldName="billing-frequency"
                options={billingFrequencyOptions}
                selectedValue={customBillingFrequency?.toString() || ""}
                onSelection={(value) => {
                  onCustomBillingFrequencyChange(Number(value));
                }}
                placeholder={t("contractTerms.selectBillingFrequency")}
                variant="form"
                className="w-full"
              />
            </ContractField>
          )}

          {isCustomBillingFrequencySelected && (
            <PaymentSchedule
              totalAmount={total}
              billingStartDate={billingStartDate}
              currency={currency}
              mode="recurring"
              paymentType={paymentType}
              scheduledPayments={scheduledPayments}
              onRecurringEqualPaymentChange={(
                billingEvery: number,
                period: EqPaymentsBillingFrequency
              ) => {
                onSplitPaymentDataChange({
                  numberOfPayments: computeRecurringPaymentCount(
                    serviceDurationInMonths,
                    billingEvery,
                    period
                  ),
                  billingFrequencyLabel: getPeriodLabel(billingEvery, period),
                  billingFrequency: period,
                  billingEvery
                });
              }}
              onScheduledPaymentsChange={onScheduledPaymentsChange}
              onPaymentTypeChange={onPaymentTypeChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default BillingScheduleSection;
